import * as React from 'react';
import * as api from '../services/api-client';

/**
 * API Key state management using React Context + useReducer.
 * Manages listing, generating, and revoking API keys for a subdomain.
 * Uses optimistic updates with revert on failure pattern.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ApiKey {
  readonly keyId: string;
  readonly description: string;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly revoked: boolean;
}

export interface GeneratedApiKey {
  readonly keyId: string;
  readonly apiKey: string;
  readonly expiresAt: string;
  readonly subdomain: string;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface ApiKeyState {
  readonly keys: readonly ApiKey[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly generatedKey: GeneratedApiKey | null;
  readonly isGenerating: boolean;
}

export const initialApiKeyState: ApiKeyState = {
  keys: [],
  isLoading: false,
  error: null,
  generatedKey: null,
  isGenerating: false,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type ApiKeyAction =
  | { readonly type: 'KEYS_LOADING' }
  | { readonly type: 'KEYS_LOADED'; readonly payload: readonly ApiKey[] }
  | { readonly type: 'KEYS_ERROR'; readonly payload: string }
  | { readonly type: 'KEY_GENERATING' }
  | { readonly type: 'KEY_GENERATED'; readonly payload: { key: GeneratedApiKey; record: ApiKey } }
  | { readonly type: 'KEY_GENERATE_ERROR'; readonly payload: string }
  | { readonly type: 'KEY_REVOKE_OPTIMISTIC'; readonly payload: string }
  | { readonly type: 'KEY_REVOKE_CONFIRMED' }
  | { readonly type: 'KEY_REVOKE_REVERT'; readonly payload: { previousKeys: readonly ApiKey[] } }
  | { readonly type: 'KEY_DISMISS_GENERATED' };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function apiKeyReducer(state: ApiKeyState, action: ApiKeyAction): ApiKeyState {
  switch (action.type) {
    case 'KEYS_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'KEYS_LOADED':
      return { ...state, keys: action.payload, isLoading: false, error: null };
    case 'KEYS_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    case 'KEY_GENERATING':
      return { ...state, isGenerating: true, error: null, generatedKey: null };
    case 'KEY_GENERATED':
      return {
        ...state,
        isGenerating: false,
        generatedKey: action.payload.key,
        keys: [...state.keys, action.payload.record],
      };
    case 'KEY_GENERATE_ERROR':
      return { ...state, isGenerating: false, error: action.payload };
    case 'KEY_REVOKE_OPTIMISTIC':
      return {
        ...state,
        keys: state.keys.map((k) =>
          k.keyId === action.payload ? { ...k, revoked: true } : k,
        ),
      };
    case 'KEY_REVOKE_CONFIRMED':
      return state;
    case 'KEY_REVOKE_REVERT':
      return { ...state, keys: action.payload.previousKeys };
    case 'KEY_DISMISS_GENERATED':
      return { ...state, generatedKey: null };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface ApiKeyContextValue {
  readonly state: ApiKeyState;
  readonly loadKeys: (subdomainId: string) => Promise<void>;
  readonly generateKey: (subdomainId: string, lifetime?: string, description?: string) => Promise<void>;
  readonly revokeKey: (subdomainId: string, keyId: string) => Promise<void>;
  readonly dismissGeneratedKey: () => void;
}

const ApiKeyContext = React.createContext<ApiKeyContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface ApiKeyProviderProps {
  readonly children: React.ReactNode;
}

/**
 * ApiKeyProvider manages API key CRUD for the currently selected subdomain.
 * Uses optimistic updates with revert on failure.
 */
export function ApiKeyProvider({ children }: ApiKeyProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(apiKeyReducer, initialApiKeyState);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  const loadKeys = React.useCallback(async (subdomainId: string): Promise<void> => {
    dispatch({ type: 'KEYS_LOADING' });

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 3000];

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const data = await api.get<{ keys: ApiKey[] }>(`/user/subdomains/${subdomainId}/keys`);
        dispatch({ type: 'KEYS_LOADED', payload: data.keys });
        return;
      } catch (err) {
        const isRetryable =
          err instanceof api.NetworkError ||
          (err instanceof api.ApiErrorResponse && err.status >= 500);

        if (isRetryable && attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
          continue;
        }

        dispatch({
          type: 'KEYS_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to load API keys',
        });
        return;
      }
    }
  }, []);

  const generateKey = React.useCallback(
    async (subdomainId: string, lifetime?: string, description?: string): Promise<void> => {
      dispatch({ type: 'KEY_GENERATING' });

      const MAX_RETRIES = 2;
      const RETRY_DELAYS = [1500, 3000];

      const body: Record<string, string> = {};
      if (lifetime) body.lifetime = lifetime;
      if (description) body.description = description;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const data = await api.post<GeneratedApiKey>(`/user/subdomains/${subdomainId}/keys`, body);
          const record: ApiKey = {
            keyId: data.keyId,
            description: description ?? '',
            expiresAt: data.expiresAt,
            createdAt: new Date().toISOString(),
            revoked: false,
          };
          dispatch({ type: 'KEY_GENERATED', payload: { key: data, record } });
          return;
        } catch (err) {
          const isRetryable =
            err instanceof api.NetworkError ||
            (err instanceof api.ApiErrorResponse && err.status >= 500);

          if (isRetryable && attempt < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
            continue;
          }

          dispatch({
            type: 'KEY_GENERATE_ERROR',
            payload: err instanceof Error ? err.message : 'Failed to generate API key',
          });
          return;
        }
      }
    },
    [],
  );

  const revokeKey = React.useCallback(
    async (subdomainId: string, keyId: string): Promise<void> => {
      const previousKeys = stateRef.current.keys;
      dispatch({ type: 'KEY_REVOKE_OPTIMISTIC', payload: keyId });
      try {
        await api.del(`/user/subdomains/${subdomainId}/keys/${keyId}`);
        dispatch({ type: 'KEY_REVOKE_CONFIRMED' });
      } catch {
        dispatch({ type: 'KEY_REVOKE_REVERT', payload: { previousKeys } });
      }
    },
    [],
  );

  const dismissGeneratedKey = React.useCallback((): void => {
    dispatch({ type: 'KEY_DISMISS_GENERATED' });
  }, []);

  const contextValue = React.useMemo(
    (): ApiKeyContextValue => ({
      state,
      loadKeys,
      generateKey,
      revokeKey,
      dismissGeneratedKey,
    }),
    [state, loadKeys, generateKey, revokeKey, dismissGeneratedKey],
  );

  return React.createElement(ApiKeyContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access API key state and actions.
 * Must be used within an ApiKeyProvider.
 */
export function useApiKeys(): ApiKeyContextValue {
  const context = React.useContext(ApiKeyContext);
  if (!context) {
    throw new Error('useApiKeys must be used within an ApiKeyProvider');
  }
  return context;
}
